import React, { useState } from "react";
import * as _ from "underscore";
import * as moment from "moment";

import BaseTable, { EditingState } from "web_modules/shared/BaseTable";
import stateOptions from "web_modules/forms/data/stateOptions";
import { DateAsString } from "web_modules/shared/mortgage_definitions";
import { ownershipOptions } from "web_modules/forms/data/ownershipOptions";
import getHandlers from "web_modules/shared/FormHelpers/eventHandlers";
import Validations from "web_modules/shared/FormHelpers/validation";
import {
  TextInput,
  Modal,
  DatePicker,
  DatePickerInput,
  Form,
  FormGroup,
  Select,
  SelectItem,
  Loading,
  InlineNotification,
} from "carbon-components-react";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { LoadingState } from "underwriter_dashboard/shared/types";
import APIClient from "web_modules/APIClient";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import BorrowerEntityUtil, {
  borrowerEntitySelectOptionsFromApp,
} from "@eave/shared/utils/borrowers";
import { BorrowerEntity, PastResidenceOccupancy, PastResidence } from "@eave/shared/types/api";
import { makeHeaderData } from "web_modules/shared/BaseTable/helpers";

import { addressToStr, appToBorrowerName } from "../shared";

require("../../../assets/stylesheets/components/underwriter_dashboard/hurdle_editors/history.scss");

const EMDASH = <span className="history__emdash">—</span>;

interface AddressShape {
  id: number;
  borrower_entity: BorrowerEntity;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;

  borrower1_occupancy: null | PastResidenceOccupancy;
  borrower2_occupancy: null | PastResidenceOccupancy;
  borrower3_occupancy: null | PastResidenceOccupancy;
  borrower4_occupancy: null | PastResidenceOccupancy;
  move_in_date?: DateAsString;

  past_residence_id: number;
}

const HistoryEditor: React.FC<HurdleProps> = ({ app, hurdle, onHide }: HurdleProps) => {
  // UI-related states
  const [addressModalIsOpen, updateAddressModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [editingMode, updateEditingMode] = useState(EditingState.CREATE);
  const [loadingState, updateLoadingState] = useState(LoadingState.INACTIVE);

  // Batch deletion hooks
  const [selectedRows, setSelectedRows] = useState([]);
  const [batchDeleteModalIsOpen, setBatchDeleteModalIsOpen] = useState(false);

  // Error message for the inline notification
  const [errorMessage, updateErrorMessage] = useState("");

  // Form field values
  const [currentBorrowerEntity, updateCurrentBorrowerEntity] = useState<BorrowerEntity>(1);
  const [currentBorrowerOccupancy, updateCurrentBorrowerOccupancy] = useState(null);

  // pastResidences is used
  const [pastResidences, updatePastResidences] = useState(app.past_residences as PastResidence[]);

  const initialAddressState: AddressShape = {
    id: null,
    borrower_entity: null,
    address_line1: null,
    address_line2: null,
    city: null,
    state: null,
    postal_code: null,

    borrower1_occupancy: null,
    borrower2_occupancy: null,
    borrower3_occupancy: null,
    borrower4_occupancy: null,

    past_residence_id: null,
  };

  // Holds the values for the form
  const [addressFormState, updateAddressFormState] = useState<AddressShape | null>(
    initialAddressState,
  );
  const [invalidFields, updateInvalidFields] = useState([]);

  const { handleTextInputChange, handleSelectChange } = getHandlers(
    addressFormState,
    updateAddressFormState,
    invalidFields,
    updateInvalidFields,
  );

  const resetAddressForm = () => {
    updateAddressFormState(initialAddressState);
    updateCurrentBorrowerOccupancy(null);
    updateCurrentBorrowerEntity(BorrowerEntity.Borrower1);
  };

  const borrowerNameFromEntity = (entity: BorrowerEntity) => {
    return appToBorrowerName(app, entity);
  };

  const translateOwnershipStatus = raw => {
    const translated = ownershipOptions.find(o => o.value === raw);
    return translated === undefined ? raw : translated.label;
  };

  // Holds the conditional logic for how data should be saved with borrower_entity values of 1/2/3
  const constructFormData = (): AddressShape => {
    const formData: AddressShape = { ...addressFormState };
    const borrowerEntity = new BorrowerEntityUtil(currentBorrowerEntity);

    const { Borrower1, Borrower2, Borrower3, Borrower4 } = BorrowerEntity;

    if (borrowerEntity.has(Borrower3)) {
      formData.borrower3_occupancy = {
        borrower_entity: BorrowerEntity.Borrower3,
        ...currentBorrowerOccupancy,
      };
    }
    if (borrowerEntity.has(Borrower4)) {
      formData.borrower4_occupancy = {
        borrower_entity: BorrowerEntity.Borrower4,
        ...currentBorrowerOccupancy,
      };
    }
    if (borrowerEntity.has(Borrower1)) {
      formData.borrower1_occupancy = {
        borrower_entity: BorrowerEntity.Borrower1,
        ...currentBorrowerOccupancy,
      };
    }

    if (borrowerEntity.has(Borrower2)) {
      formData.borrower2_occupancy = {
        borrower_entity: Borrower2,
        ...currentBorrowerOccupancy,
      };
    }

    formData.borrower_entity = borrowerEntity.value();

    return formData;
  };

  const deleteOccupancyRequest = (rawRowData: any) => {
    const residenceID = rawRowData.id;

    // past_residence_occupancies are for borrower_entity 1 or 2 --
    // not for 3 (both borrowers).
    let occupancyID;
    if (rawRowData.borrower_entity === BorrowerEntity.Borrower1) {
      occupancyID = rawRowData.borrower1_occupancy.id;
    } else if (rawRowData.borrower_entity === BorrowerEntity.Borrower2) {
      occupancyID = rawRowData.borrower2_occupancy.id;
    } else if (rawRowData.borrower_entity === BorrowerEntity.Borrower3) {
      occupancyID = rawRowData.borrower3_occupancy.id;
    } else if (rawRowData.borrower_entity === BorrowerEntity.Borrower4) {
      occupancyID = rawRowData.borrower4_occupancy.id;
    } else {
      throw new Error("borrower_entity must be either '1' or '2' or '4' or '8'");
    }

    return APIClient.deleteRequest(
      `/uw/${app.id}/past_residences/${residenceID}/past_residence_occupancy/${occupancyID}`,
    );
  };

  const deleteOccupancy = (rawRowData: any) => {
    updateLoadingState(LoadingState.ACTIVE);

    deleteOccupancyRequest(rawRowData)
      .then(() => {
        updateLoadingState(LoadingState.FINISHED);
        // Just refresh after a delete to reload data
        window.location.reload();
      })
      .catch(err => {
        updateLoadingState(LoadingState.FINISHED);
        updateErrorMessage(err);
      });
  };

  const createAddress = () => {
    const formData = constructFormData();

    if (invalidFields.length !== 0) {
      updateErrorMessage("There are invalid fields, please check again");
      return;
    }

    // Inputs are valid, let's prepare to hit the API
    updateErrorMessage("");
    updateLoadingState(LoadingState.ACTIVE);

    APIClient.post(`/uw/${app.id}/past_residences`, formData)
      .then((resp: PastResidence) => {
        updatePastResidences([...pastResidences, resp]);
        resetAddressForm();
        updateLoadingState(LoadingState.FINISHED);
        updateAddressModalIsOpen(false);
      })
      .catch(err => {
        updateLoadingState(LoadingState.FINISHED);
        updateErrorMessage(err);
      });
  };

  const updateAddress = () => {
    const formData = constructFormData();

    if (invalidFields.length !== 0) {
      updateErrorMessage("There are invalid fields, please check again");
      return;
    }

    // Clear any errors
    updateErrorMessage("");

    // Inputs are valid, let's prepare to hit the API
    updateLoadingState(LoadingState.ACTIVE);

    APIClient.put(`/uw/${app.id}/past_residences/${formData.past_residence_id}`, formData)
      .then((resp: PastResidence) => {
        const updatedAddressID = _.findIndex(pastResidences, { id: resp.id });

        const updatedResidences = [...pastResidences];
        updatedResidences.splice(updatedAddressID, 1, resp);

        updatePastResidences(updatedResidences);
        resetAddressForm();
        updateLoadingState(LoadingState.FINISHED);
        updateAddressModalIsOpen(false);
      })
      .catch(err => {
        updateLoadingState(LoadingState.FINISHED);
        updateErrorMessage(err);
      });
  };

  const renderAddressHistory = () => {
    // Sort by move_in_date, then by borrower
    const sortedAddresses = _.chain(pastResidences)
      .map((residence: PastResidence) => {
        let occupancies = [];
        if (residence.borrower1_occupancy) {
          occupancies = occupancies.concat({
            ...residence,
            ...residence.borrower1_occupancy,
          });
        }
        if (residence.borrower2_occupancy) {
          occupancies = occupancies.concat({
            ...residence,
            ...residence.borrower2_occupancy,
          });
        }
        if (residence.borrower3_occupancy) {
          occupancies = occupancies.concat({
            ...residence,
            ...residence.borrower3_occupancy,
          });
        }
        if (residence.borrower4_occupancy) {
          occupancies = occupancies.concat({
            ...residence,
            ...residence.borrower4_occupancy,
          });
        }
        return occupancies;
      })
      .flatten()
      .sortBy((occupancy: PastResidenceOccupancy) => -1 * +moment(occupancy.move_in_date || 0))
      .sortBy((occupancy: PastResidenceOccupancy) => occupancy.borrower_entity) // higher priority field
      .value();

    const sortedElToRawRowDataEl = (sortedEl: any) => ({
      ...sortedEl,
      id: sortedEl.past_residence_id,
    });

    return (
      <div style={{ marginBottom: "2rem" }}>
        <BaseTable
          isSortable
          title="Address History (by Borrower)"
          emptyRowText="No Addresses"
          openModal={() => updateAddressModalIsOpen(true)}
          openDeleteModal={() => setDeleteModalIsOpen(true)}
          updateFormState={data => {
            const {
              borrower_entity,
              borrower1_occupancy,
              borrower2_occupancy,
              borrower3_occupancy,
              borrower4_occupancy,
            } = data;
            const { Borrower1, Borrower2, Borrower3, Borrower4 } = BorrowerEntity;

            const borrowerEntity = new BorrowerEntityUtil(borrower_entity);

            // Update the general form state
            updateAddressFormState(data);

            updateCurrentBorrowerEntity(borrower_entity);

            if (borrowerEntity.has(Borrower1)) {
              updateCurrentBorrowerOccupancy(borrower1_occupancy);
            } else if (borrowerEntity.has(Borrower2)) {
              updateCurrentBorrowerOccupancy(borrower2_occupancy);
            } else if (borrowerEntity.has(Borrower3)) {
              updateCurrentBorrowerOccupancy(borrower3_occupancy);
            } else if (borrowerEntity.has(Borrower4)) {
              updateCurrentBorrowerOccupancy(borrower4_occupancy);
            }
          }}
          disableBatchEdit
          onBatchDelete={(selectedRawRowsData: any[]) => {
            setSelectedRows(selectedRawRowsData);
            setBatchDeleteModalIsOpen(true);
          }}
          updateEditingMode={updateEditingMode}
          rawRowData={sortedAddresses.map(row => sortedElToRawRowDataEl(row))}
          rowData={sortedAddresses.map(row => {
            const {
              id,
              borrower_entity,
              // move_in_date,
              ownership_status,
              address_line1,
              address_line2,
              city,
              state,
              postal_code,
              borrower1_occupancy,
              borrower2_occupancy,
              borrower3_occupancy,
              borrower4_occupancy,
            } = row;

            const moveInDateProperty =
              borrower1_occupancy?.move_in_date ||
              borrower2_occupancy?.move_in_date ||
              borrower3_occupancy?.move_in_date ||
              borrower4_occupancy?.move_in_date ||
              null;

            return {
              id: String(id),
              borrower: borrowerNameFromEntity(borrower_entity) || EMDASH,
              move_in_date: moveInDateProperty
                ? moment.utc(moveInDateProperty).format("YYYY-MM-DD")
                : EMDASH,

              // move_in_date: move_in_date ? moment.utc(move_in_date).format("YYYY-MM-DD") : EMDASH,
              address: addressToStr(address_line1, address_line2, city, state, postal_code),
              ownership_status: translateOwnershipStatus(ownership_status) || EMDASH,
            };
          })}
          headerData={makeHeaderData({
            borrower: "Borrower",
            move_in_date: "Move In Date",
            address: "Address",
            ownership_status: "Ownership Status",
          })}
        />
      </div>
    );
  };

  // Update the current borrower entity
  const handleBorrowerChange = evt => {
    updateCurrentBorrowerEntity(parseInt(evt.target.value, 10));
  };

  const allBorrowerEntityOptions = () => {
    const selectItems = [];
    const initialSelectOptions = borrowerEntitySelectOptionsFromApp(app);

    for (let i = 0; i < initialSelectOptions.length; i++) {
      const startingLength = selectItems.length;
      selectItems.push(
        <SelectItem text={initialSelectOptions[i].label} value={initialSelectOptions[i].value} />,
      );
      for (let j = 0; j < startingLength; j++) {
        const item = selectItems[j];
        selectItems.push(
          <SelectItem
            text={`${item.props.text} and ${initialSelectOptions[i].label}`}
            // eslint-disable-next-line no-bitwise
            value={item.props.value | initialSelectOptions[i].value}
          />,
        );
      }
    }
    return selectItems;
  };

  return (
    <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
      <Modal
        danger
        open={deleteModalIsOpen}
        modalHeading="Are you sure you'd like to delete this record?"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onBlur={() => setDeleteModalIsOpen(false)}
        onRequestClose={() => setDeleteModalIsOpen(false)}
        onRequestSubmit={() => deleteOccupancy(addressFormState)}
      />
      <Modal
        danger
        open={batchDeleteModalIsOpen}
        modalHeading="Confirm batch delete"
        primaryButtonText={`Delete ${selectedRows.length} address${
          selectedRows.length > 1 ? "es" : ""
        }`}
        secondaryButtonText="Cancel"
        onBlur={() => setBatchDeleteModalIsOpen(false)}
        onRequestClose={() => setBatchDeleteModalIsOpen(false)}
        onRequestSubmit={() => {
          updateLoadingState(LoadingState.ACTIVE);

          const promises = selectedRows.map(data => deleteOccupancyRequest(data));

          Promise.all(promises).then(() => {
            updateLoadingState(LoadingState.FINISHED);
            setSelectedRows([]);

            window.location.reload();
          });
        }}
      >
        <p style={{ fontSize: "18px", marginBottom: "10px" }}>
          The following addresses will be deleted:{" "}
        </p>
        <ul>
          {selectedRows &&
            selectedRows.map(address => {
              const { address_line1, address_line2, city, state, postal_code } = address;
              return (
                <li key={address_line1} style={{ marginLeft: "1rem" }}>
                  - {addressToStr(address_line1, address_line2, city, state, postal_code)}
                </li>
              );
            })}
        </ul>
      </Modal>

      <Modal
        open={addressModalIsOpen}
        modalHeading={
          editingMode === EditingState.CREATE ? "Add Address History" : "Update Address"
        }
        primaryButtonText={editingMode === EditingState.CREATE ? "Save" : "Update"}
        secondaryButtonText="Cancel"
        selectorPrimaryFocus="#borrower-name"
        onRequestSubmit={() => {
          if (editingMode === EditingState.CREATE) {
            createAddress();
          }
          if (editingMode === EditingState.UPDATE) {
            updateAddress();
          }
        }}
        onRequestClose={() => {
          resetAddressForm();
          updateAddressModalIsOpen(false);
        }}
        onBlur={() => {
          resetAddressForm();
          updateAddressModalIsOpen(false);
        }}
      >
        {/* Loading spinner for table */}
        <Loading active={loadingState === LoadingState.ACTIVE} />

        {errorMessage.length > 0 && (
          <InlineNotification
            kind="error"
            notificationType="inline"
            role="alert"
            statusIconDescription="Error"
            subtitle={errorMessage}
            title="Error"
            onCloseButtonClick={() => {
              updateErrorMessage("");
            }}
          />
        )}

        <Form className="some-class" onSubmit={() => {}}>
          <FormGroup legendText="Borrower Information">
            <Select
              id="borrower-name"
              value={currentBorrowerEntity}
              invalidText="A valid value is required"
              labelText="Borrower Name"
              onBlur={handleBorrowerChange}
              onChange={handleBorrowerChange}
              disabled={editingMode === EditingState.UPDATE}
            >
              {allBorrowerEntityOptions()}
            </Select>
          </FormGroup>
          <div style={{ marginBottom: "10px" }} />
          <FormGroup legendText="Past Residence Occupancy info">
            <DatePicker
              id="date-picker"
              datePickerType="single"
              dateFormat="Y-m-d"
              style={{ marginBottom: "10px" }}
              value={currentBorrowerOccupancy && currentBorrowerOccupancy.move_in_date}
              onChange={event => {
                // Update the current borrower occupancy data to include the move_in_date as expected
                updateCurrentBorrowerOccupancy({
                  ...currentBorrowerOccupancy,
                  move_in_date: moment(event[0]).format("YYYY-MM-DD"),
                });
              }}
            >
              <DatePickerInput
                id="date-picker-input-id"
                invalid={invalidFields.includes("move_in_date")}
                iconDescription="Select a move in date"
                labelText="Move In Date"
                placeholder="yyyy-mm-dd"
                type="text"
              />
            </DatePicker>
            <div style={{ marginBottom: "10px" }} />
            <Select
              id="ownership_status"
              name="ownership_status"
              value={(currentBorrowerOccupancy && currentBorrowerOccupancy.ownership_status) || ""}
              invalid={invalidFields.includes("ownership_status")}
              invalidText="A valid value is required"
              labelText="Ownership Status"
              style={{ marginBottom: "10px" }}
              onChange={event => {
                const resetRentAmount: Boolean = event.target.value !== "rent";

                // When resetting a value to a non-rent value, reset the rent amount
                const diff = resetRentAmount
                  ? {
                      ...currentBorrowerOccupancy,
                      ownership_status: event.target.value,
                      last_monthly_rent_amount: null,
                    }
                  : {
                      ...currentBorrowerOccupancy,
                      ownership_status: event.target.value,
                    };

                updateCurrentBorrowerOccupancy(diff);
              }}
            >
              <SelectItem text="Select Ownership Status.." value="" />
              {ownershipOptions.map(o => (
                <SelectItem key={o.label} text={o.label} value={o.value} />
              ))}
            </Select>
            <div style={{ marginBottom: "10px" }} />
            {currentBorrowerOccupancy && currentBorrowerOccupancy.ownership_status === "rent" && (
              <TextInput
                id="last_monthly_rent_amount"
                name="last_monthly_rent_amount"
                value={
                  (currentBorrowerOccupancy && currentBorrowerOccupancy.last_monthly_rent_amount) ||
                  ""
                }
                placeholder="Enter rent amount"
                invalid={invalidFields.includes("last_monthly_rent_amount")}
                invalidText="A valid value is required"
                labelText="Last Monthly Rent Amount"
                style={{ marginBottom: "10px" }}
                onChange={event =>
                  updateCurrentBorrowerOccupancy({
                    ...currentBorrowerOccupancy,
                    last_monthly_rent_amount: event.target.value,
                  })
                }
              />
            )}
          </FormGroup>

          <FormGroup legendText="Past Residence Information">
            <TextInput
              id="address_line1"
              name="address_line1"
              value={(addressFormState && addressFormState.address_line1) || ""}
              labelText="Address Line 1*"
              placeholder="1 Infinite Loop"
              type="text"
              invalid={invalidFields.includes("address_line1")}
              invalidText="Enter a valid address"
              onBlur={e =>
                handleTextInputChange(e, "address_line1", [
                  Validations.IsRequired,
                  v => v.length > 4,
                ])
              }
              onChange={e =>
                handleTextInputChange(e, "address_line1", [
                  Validations.IsRequired,
                  v => v.length > 4,
                ])
              }
            />
            <div style={{ marginBottom: "10px" }} />
            <TextInput
              id="address_line2"
              name="address_line2"
              value={(addressFormState && addressFormState.address_line2) || ""}
              labelText="Address Line 2"
              placeholder="Floor 2"
              type="text"
              invalid={invalidFields.includes("address_line2")}
              invalidText="Enter a valid address line 2"
              onChange={e => handleTextInputChange(e, "address_line2")}
            />
            <div style={{ marginBottom: "10px" }} />
            <TextInput
              id="city"
              name="city"
              value={(addressFormState && addressFormState.city) || ""}
              labelText="City"
              placeholder="Cupertino"
              type="text"
              invalid={invalidFields.includes("city")}
              invalidText="Enter a valid city"
              onChange={e =>
                handleTextInputChange(e, "city", [Validations.IsRequired, v => v.length >= 3])
              }
            />
            <div style={{ marginBottom: "10px" }} />
            <Select
              id="state"
              name="state"
              value={(addressFormState && addressFormState.state) || ""}
              labelText="State"
              invalid={invalidFields.includes("state")}
              invalidText="Enter a valid state"
              onChange={e => handleSelectChange(e, "state", [Validations.IsRequired])}
            >
              <SelectItem text="Select State" value="" />
              {stateOptions.map(state => (
                <SelectItem key={state.label} text={state.label} value={state.value} />
              ))}
            </Select>
            <div style={{ marginBottom: "10px" }} />
            <TextInput
              id="postal_code"
              name="postal_code"
              value={(addressFormState && addressFormState.postal_code) || ""}
              invalid={invalidFields.includes("postal_code")}
              invalidText="Enter a zip code (either 5-digit or hyphenated 9-digit form)"
              labelText="Postal Code"
              placeholder="90210"
              type="text"
              onChange={e =>
                handleTextInputChange(e, "postal_code", [
                  Validations.IsRequired,
                  Validations.IsPostalCode,
                ])
              }
            />
            <div style={{ marginBottom: "10px" }} />
          </FormGroup>
        </Form>
      </Modal>
      {renderAddressHistory()}
    </HurdleView>
  );
};

export default HistoryEditor;
